import { ActionItem, ActionItemStyle, ButtonActionItem } from '@stylehaven/components';
import { Observable, Observer, combineLatest, isObservable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { EMPTY_OBSERVER } from '../../../helpers';

export class RecordActionItem<T> {
    _labelFn: (record: T) => Observable<string>;

    protected _disabledInfoMsg?: string = undefined;

    callback: (record: T, observer: Observer<any>) => void = () => {};

    constructor(label: string | Observable<string>) {
        if (isObservable(label)) {
            this._labelFn = () => label;
        } else {
            this._labelFn = () => of(label);
        }
    }

    private _predicate: (record: T) => Observable<boolean | undefined> = () => of(true);

    get predicate(): (record: T) => Observable<boolean | undefined> {
        return this._predicate;
    }

    set enablePredicate(simplePredicate: (record: T) => boolean | undefined) {
        this._predicate = record => of(simplePredicate(record));
    }

    set dynamicEnablePredicate(enablePredicate: (record: T) => Observable<boolean | undefined>) {
        this._predicate = enablePredicate;
    }

    set labelFn(simpleFn: (record: T) => string) {
        this._labelFn = record => of(simpleFn(record));
    }

    set dynamicLabelFn(dynamicFn: (record: T) => Observable<string>) {
        this._labelFn = dynamicFn;
    }

    set disabledInfoMsg(msg: string) {
        this._disabledInfoMsg = msg;
    }

    public toActionItem(record: T): Observable<ActionItem | null>;
    public toActionItem(record: T, observer: Observer<any>): Observable<ActionItem | null>;
    public toActionItem(
        record: T,
        observer: Observer<any>,
        actionItemStyle: ActionItemStyle
    ): Observable<ActionItem | null>;

    public toActionItem(
        record: T,
        observer: Observer<any>,
        actionItemTemplate: Partial<ActionItem>
    ): Observable<ActionItem | null>;

    public toActionItem(
        record: T,
        observer: Observer<any> = EMPTY_OBSERVER,
        actionItemTemplate: ActionItemStyle | Partial<ActionItem> = ActionItemStyle.Primary
    ): Observable<ActionItem | null> {
        let template: Partial<ActionItem> = {};
        if (this.isActionItemStyle(actionItemTemplate)) {
            template = {
                style: actionItemTemplate
            };
        } else {
            template = actionItemTemplate;
        }

        return combineLatest([this._predicate(record), this._labelFn(record)]).pipe(
            map(([shouldEnable, label]) => this.createActionItem(shouldEnable, label, record, observer, template))
        );
    }

    protected createActionItem(
        shouldEnable: boolean | undefined,
        label: string,
        record: T,
        observer: Observer<any>,
        template: Partial<ActionItem>
    ): ButtonActionItem | null {
        if (shouldEnable === undefined) {
            return null;
        }

        return {
            ...template,
            label,
            disabled: !shouldEnable,
            disabledInfoMsg: this._disabledInfoMsg,
            eventListener: event => {
                if (template.eventListener) {
                    template.eventListener(event);
                }

                this.callback(record, observer);
            }
        };
    }

    private isActionItemStyle(item: any): item is ActionItemStyle {
        return Object.values(ActionItemStyle).includes(item);
    }
}
